import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiCall } from '../api/client';
import Markdown from './Markdown';

// The runtime tutor chat (Phase 2 — Q&A). Explicit modes (plan §9) so the learner
// understands when the AI engages: "Ask a question" is live; writing-evaluation and
// fresh-examples arrive in later phases and are shown as coming soon.
const MODES = [
  { id: 'qa', label: 'Ask a question', enabled: true },
  { id: 'evaluate', label: 'Check my writing', enabled: false },
  { id: 'example', label: 'Another example', enabled: false },
];

// `intro` is the lesson's frozen content (explanation / reading passage), presented as
// the tutor's opening messages: [{ label?, content }] where content is markdown. It is
// display-only — never sent to the API (the backend prompt already carries the lesson
// scope and passage) — and shown to everyone, including non-admins, since it IS the
// lesson content, not the (gated) live chat.
export default function TutorChat({ courseId, lessonId, isAdmin, enabled = isAdmin, drillQuestion, showModes = true, intro = [] }) {
  const { token } = useAuth();
  const [messages, setMessages] = useState([]); // { role: 'user' | 'assistant', content }
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo?.({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  async function send(text) {
    const message = (text ?? input).trim();
    if (!message || sending) return;
    setError(null);
    setInput('');

    // Send prior turns as history (the API is stateless); append the new user turn.
    const history = messages;
    setMessages(prev => [...prev, { role: 'user', content: message }]);
    setSending(true);
    try {
      const { reply } = await apiCall(
        'POST', `/courses/${courseId}/lessons/${lessonId}/tutor`,
        { message, history, mode: 'qa', ...(drillQuestion ? { drillQuestion } : {}) }, token
      );
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      setError(err.message || 'The tutor could not respond.');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="bg-white rounded-xl border border-amber-100 overflow-hidden">
      <div className="px-4 py-3 border-b border-amber-100 flex items-center gap-2">
        <span className="text-xl">🐝</span>
        <h3 className="font-semibold text-amber-800">Tutor</h3>
        <span className="text-xs text-amber-400">stays within what you've learned</span>
      </div>

      {/* The tutor's opening messages: the lesson's frozen content, rendered as tutor
          bubbles (markdown, no LLM). Outside the capped scroll area below so the
          learner reads it in the page flow, not inside a small scrollbox. */}
      {intro.length > 0 && (
        <div className="px-4 pt-3 space-y-3">
          {intro.map((m, i) => (
            <div key={i} className="flex justify-start">
              <div className="max-w-[95%] rounded-2xl rounded-bl-sm bg-amber-50 text-amber-900 px-4 py-2 text-sm">
                {m.label && <p className="text-xs uppercase tracking-wide text-amber-400 mt-2 mb-1">{m.label}</p>}
                <Markdown>{m.content}</Markdown>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Live chat is admin-only while the tutor is in limited preview — the LLM costs
          real money, so it's kept off-limits to the general public for now. (The
          backend enforces this too.) */}
      {!enabled ? (
        <p className="px-4 py-3 text-sm text-amber-400">
          The AI tutor is in limited preview — asking questions is coming soon.
        </p>
      ) : (
        <>
          {/* Mode chips — only Q&A is active in this phase. */}
          {showModes && <div className="px-4 pt-3 flex flex-wrap gap-2">
            {MODES.map(m => (
              <span
                key={m.id}
                className={`text-xs px-2.5 py-1 rounded-full border ${
                  m.enabled
                    ? 'bg-amber-100 border-amber-200 text-amber-700'
                    : 'bg-gray-50 border-gray-200 text-gray-300'
                }`}
                title={m.enabled ? undefined : 'Coming soon'}
              >
                {m.label}{m.enabled ? '' : ' · soon'}
              </span>
            ))}
          </div>}

          {/* Conversation */}
          <div ref={scrollRef} className="px-4 py-3 max-h-80 overflow-y-auto space-y-3">
            {messages.length === 0 && !sending && (
              <div className="text-sm text-amber-500">
                <p>Ask anything about this lesson — grammar, word choice, or how something works.</p>
              </div>
            )}
            {/* Tutor replies are markdown (the model bolds/lists things); the
                learner's own text stays verbatim. */}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                  m.role === 'user'
                    ? 'bg-amber-500 text-white rounded-br-sm whitespace-pre-wrap'
                    : 'bg-amber-50 text-amber-900 rounded-bl-sm'
                }`}>
                  {m.role === 'user' ? m.content : <Markdown>{m.content}</Markdown>}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-amber-50 text-amber-400 rounded-2xl rounded-bl-sm px-3 py-2 text-sm">
                  thinking…
                </div>
              </div>
            )}
          </div>

          {error && <p className="px-4 pb-1 text-sm text-rose-600">{error}</p>}

          {/* Composer */}
          <div className="px-4 py-3 border-t border-amber-100 flex gap-2">
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') send(); }}
              placeholder="Ask a question about this lesson…"
              disabled={sending}
              className="flex-1 px-3 py-2 rounded-lg border border-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:bg-gray-50"
            />
            <button
              onClick={() => send()}
              disabled={sending || !input.trim()}
              className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium transition disabled:opacity-50"
            >
              Send
            </button>
          </div>
        </>
      )}
    </div>
  );
}
